import { useEffect, useState } from 'react';
import { driverSupabase as supabase } from '@/integrations/supabase/driver-client';
import { Megaphone, X } from 'lucide-react';
import { useDriverTheme } from '@/hooks/useDriverTheme';

interface Announcement {
  id: string;
  title: string;
  message: string;
  severity?: 'info' | 'warning' | 'critical';
  expires_at?: string;
}

/**
 * P2: Global admin announcements — listens to the Supabase Broadcast channel
 * `system-announcements`. Admin emits `{ event: 'announcement', payload: { ... } }`
 * and every connected driver renders the message as a dismissible top banner.
 *
 * Uses Broadcast (no DB row required), so admin can send instant ephemeral
 * notices like "Maintenance in 10 min".
 */
export default function SystemAnnouncementOverlay() {
  const { tokens } = useDriverTheme();
  const [announcement, setAnnouncement] = useState<Announcement | null>(null);

  useEffect(() => {
    const channel = supabase
      .channel('system-announcements')
      .on('broadcast', { event: 'announcement' }, ({ payload }) => {
        if (!payload?.title) return;
        setAnnouncement({
          id: payload.id ?? `${Date.now()}`,
          title: payload.title,
          message: payload.message ?? '',
          severity: payload.severity ?? 'info',
          expires_at: payload.expires_at,
        });
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  // Auto-dismiss when the announcement expires
  useEffect(() => {
    if (!announcement?.expires_at) return;
    const ms = new Date(announcement.expires_at).getTime() - Date.now();
    if (ms <= 0) {
      setAnnouncement(null);
      return;
    }
    const t = setTimeout(() => setAnnouncement(null), ms);
    return () => clearTimeout(t);
  }, [announcement?.id, announcement?.expires_at]);

  if (!announcement) return null;

  const bg =
    announcement.severity === 'critical'
      ? '#DC2626'
      : announcement.severity === 'warning'
        ? '#F59E0B'
        : '#F97316';

  return (
    <div
      className="fixed left-0 right-0 z-[90] px-3"
      style={{ top: 'calc(env(safe-area-inset-top, 0px) + 8px)' }}
    >
      <div
        className="mx-auto max-w-md rounded-2xl shadow-lg flex items-start gap-3 p-3 text-white"
        style={{ background: bg }}
      >
        <Megaphone className="h-5 w-5 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold leading-tight">{announcement.title}</p>
          {announcement.message && (
            <p className="text-xs opacity-95 mt-0.5 leading-snug">{announcement.message}</p>
          )}
        </div>
        <button
          onClick={() => setAnnouncement(null)}
          className="h-6 w-6 rounded-full bg-white/20 flex items-center justify-center shrink-0"
          aria-label="Dismiss announcement"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
